import Vue from 'vue';
import { defineStore } from 'pinia';
import _ from 'lodash';

import TimeEntryApi from '@/extensions/tickets/store/api/timeEntry';

/**
 * The booked-times list. Its own store, the way every other list in this admin
 * has one — the ticket store already carries the queue, the counts, the running
 * sessions and the two dialogs, and a second pagination block in there would be
 * the thing nobody can find.
 */
export const useTimeEntryStore = defineStore('timeEntry', {
    state: () => ({
        loading: true,
        // Which list request is the current one. Two overlap on the first
        // render — created() asks, then the default filter set applies itself
        // and asks again — and the slower, unfiltered answer must not win.
        timeEntriesPaginatedRequest: 0,
        timeEntriesPaginated: {
            variables: {
                order: 'started_at',
                direction: 'desc',
                paginationSizes: [25, 50, 100],
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0,
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            defaultSort: null,
            // What the whole filtered set adds up to, not the page.
            summary: null,
            link: {},
            items: [],
        },
    }),

    actions: {
        addPaginatedEntriesToState(data) {
            this.timeEntriesPaginated.items = data.items;
            this.timeEntriesPaginated.pageInfo = _.cloneDeep(data.pageInfo);
            this.timeEntriesPaginated.allColumns = _.cloneDeep(data.config.allColumns);
            this.timeEntriesPaginated.link = _.cloneDeep(data.config.link);
            this.timeEntriesPaginated.showFilters = _.get(data.config, 'showFilters') || false;
            this.timeEntriesPaginated.defaultSort = _.get(data.config, 'defaultSort') || null;
            this.timeEntriesPaginated.summary = _.get(data, 'summary') || null;
        },

        defineActiveColumns(activeColumnKeys) {
            const activeColumns = [];
            _.each(this.timeEntriesPaginated.allColumns, (column) => {
                const allowed = _.find(activeColumnKeys, (value) => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.timeEntriesPaginated.activeColumns = activeColumns;
            }
        },

        defineItemsPerPage(perPage) {
            this.timeEntriesPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.timeEntriesPaginated.pageInfo.currentPage = currentPage;
        },

        defineFilterValue({ key, value }) {
            if (
                value.match === '' &&
                value.like === '' &&
                value.from === '' &&
                value.to === '' &&
                value.in.length === 0
            ) {
                Vue.delete(this.timeEntriesPaginated.filters, key);
            } else {
                Vue.set(this.timeEntriesPaginated.filters, key, value);
            }
        },

        defineSort({ key, value }) {
            this.timeEntriesPaginated.variables.order = key;
            this.timeEntriesPaginated.variables.direction = value;
        },

        defineFilterVisibility(value) {
            this.timeEntriesPaginated.showFilters = value;
        },

        updateLoading(value) {
            this.loading = value;
        },

        fetchTimeEntriesPaginated(desk) {
            const request = ++this.timeEntriesPaginatedRequest;

            return TimeEntryApi.fetchPaginated(this.timeEntriesPaginated, desk).then((data) => {
                // A newer request went out while this one was in flight, so this
                // answer describes filters nobody is looking at any more.
                if (request !== this.timeEntriesPaginatedRequest) return;

                this.addPaginatedEntriesToState(data);

                const activeColumnKeys = _.map(data.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            });
        },
    },
});
